import React, { useState, useEffect } from 'react';
import { openPrintWindow } from '../../utils/printDocument';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  PrintOutlined as PrintOutlinedIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';

const OvertimeManagement = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // États
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [filteredOvertimes, setFilteredOvertimes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  
  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
    personnelId: 'all'
  });
  
  // Formulaire
  const [formData, setFormData] = useState({
    personnelId: '',
    date: new Date().toISOString().split('T')[0],
    nombreHeures: '',
    heureDebut: '',
    heureFin: '',
    motif: '',
    tauxMajoration: '0',
    type: 'normal' // normal, nuit, dimanche, ferie
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Sélection multiple
  const [selectedOvertimes, setSelectedOvertimes] = useState([]);

  // Charger les données au montage
  useEffect(() => {
    fetchData();
  }, [currentUser]);

  // Pré-sélectionner le personnel si on vient depuis la fiche
  useEffect(() => {
    if (location.state?.personnelId && personnel.length > 0) {
      setFilters(prev => ({
        ...prev,
        personnelId: location.state.personnelId
      }));
    }
  }, [location.state, personnel]);

  // Appliquer les filtres
  useEffect(() => {
    applyFilters();
  }, [overtimes, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les informations de l'établissement
      if (userData?.establishmentId) {
        const estabDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
        if (estabDoc.exists()) {
          setEstablishmentInfo(estabDoc.data());
        }
      }

      // Récupérer le personnel du chef de service
      const personnelQuery = query(
        collection(db, 'users'),
        where('chefServiceId', '==', currentUser.uid),
        where('role', '==', 'personnel')
      );
      const personnelSnapshot = await getDocs(personnelQuery);
      const personnelData = personnelSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPersonnel(personnelData);

      // Récupérer les heures supplémentaires
      const overtimesQuery = query(
        collection(db, 'overtimes'),
        where('chefServiceId', '==', currentUser.uid)
      );
      const overtimesSnapshot = await getDocs(overtimesQuery);
      let overtimesData = overtimesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Trier côté client pour éviter les problèmes d'index
      overtimesData.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return dateB - dateA; // Plus récentes en premier
      });
      
      setOvertimes(overtimesData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des données',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...overtimes];

    // Filtre par mois et année
    filtered = filtered.filter(overtime => {
      const overtimeDate = overtime.date?.toDate ? overtime.date.toDate() : new Date(overtime.date);
      return overtimeDate.getMonth() === filters.month && 
             overtimeDate.getFullYear() === filters.year;
    });

    // Filtre par personnel
    if (filters.personnelId !== 'all') {
      filtered = filtered.filter(overtime => overtime.personnelId === filters.personnelId);
    }

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(overtime =>
        overtime.nomComplet?.toLowerCase().includes(searchLower) ||
        overtime.motif?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOvertimes(filtered);
  };

  const handleOpenDialog = (person = null) => {
    if (person) {
      setSelectedPersonnel(person);
      setFormData({
        ...formData,
        personnelId: person.id
      });
    } else {
      setSelectedPersonnel(null);
      // Pré-sélectionner le personnel si on vient depuis la fiche
      const preselectedPersonnelId = location.state?.personnelId || '';
      setFormData({
        personnelId: preselectedPersonnelId,
        date: new Date().toISOString().split('T')[0],
        nombreHeures: '',
        heureDebut: '',
        heureFin: '',
        motif: '',
        tauxMajoration: '0',
        type: 'normal'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPersonnel(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculer automatiquement les heures si heureDebut et heureFin sont renseignés
    if (name === 'heureDebut' || name === 'heureFin') {
      const debut = name === 'heureDebut' ? value : formData.heureDebut;
      const fin = name === 'heureFin' ? value : formData.heureFin;
      
      if (debut && fin) {
        const heures = calculateHours(debut, fin);
        setFormData(prev => ({
          ...prev,
          nombreHeures: heures
        }));
      }
    }
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return '';
    const startParts = start.split(':');
    const endParts = end.split(':');
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    const diffMinutes = endMinutes - startMinutes;
    return (diffMinutes / 60).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.personnelId || !formData.date || !formData.nombreHeures || !formData.motif) {
      setSnackbar({
        open: true,
        message: 'Veuillez remplir tous les champs obligatoires',
        severity: 'error'
      });
      return;
    }

    try {
      const person = personnel.find(p => p.id === formData.personnelId);
      
      const overtimeData = {
        personnelId: formData.personnelId,
        chefServiceId: currentUser.uid,
        establishmentId: userData.establishmentId,
        nomPersonnel: person.firstName,
        prenomPersonnel: person.lastName,
        nomComplet: `${person.firstName} ${person.lastName}`,
        service: userData.service,
        date: Timestamp.fromDate(new Date(formData.date)),
        nombreHeures: parseFloat(formData.nombreHeures),
        heureDebut: formData.heureDebut || null,
        heureFin: formData.heureFin || null,
        motif: formData.motif,
        tauxMajoration: parseInt(formData.tauxMajoration),
        type: formData.type,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      };

      await addDoc(collection(db, 'overtimes'), overtimeData);

      setSnackbar({
        open: true,
        message: 'Heures supplémentaires enregistrées avec succès',
        severity: 'success'
      });

      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'enregistrement',
        severity: 'error'
      });
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOvertimes(filteredOvertimes.map(ot => ot.id));
    } else {
      setSelectedOvertimes([]);
    }
  };

  const handleSelectOne = (overtimeId) => {
    setSelectedOvertimes(prev => {
      if (prev.includes(overtimeId)) {
        return prev.filter(id => id !== overtimeId);
      } else {
        return [...prev, overtimeId];
      }
    });
  };

  const calculateSelectedStats = () => {
    const selected = filteredOvertimes.filter(ot => selectedOvertimes.includes(ot.id));
    const totalHeures = selected.reduce((sum, ot) => sum + (ot.nombreHeures || 0), 0);
    const totalMajorees = selected.reduce((sum, ot) => 
      sum + (ot.nombreHeures * (1 + ot.tauxMajoration / 100)), 0
    );
    return {
      count: selected.length,
      totalHeures: totalHeures.toFixed(2),
      totalMajorees: totalMajorees.toFixed(2)
    };
  };

  const handlePrintSelected = () => {
    const selected = filteredOvertimes.filter(ot => selectedOvertimes.includes(ot.id));
    if (selected.length === 0) {
      setSnackbar({
        open: true,
        message: 'Veuillez sélectionner au moins une ligne',
        severity: 'warning'
      });
      return;
    }

    const stats = calculateSelectedStats();

    const formatDate = (date) => {
      if (!date) return 'N/A';
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('fr-FR');
    };

    const extraCss = `
          .print-page {
            font-size: 11px;
          }
          .header {
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 10px;
            border-bottom: 2px solid #333;
          }
          .header h1 {
            margin: 0;
            font-size: 20px;
            color: #1976d2;
          }
          .stats {
            background-color: #f5f5f5;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 5px;
            display: flex;
            justify-content: space-around;
          }
          .stat-item {
            text-align: center;
          }
          .stat-label {
            font-size: 10px;
            color: #666;
            margin-bottom: 5px;
          }
          .stat-value {
            font-size: 18px;
            font-weight: bold;
            color: #1976d2;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #1976d2;
            color: white;
            font-weight: bold;
            font-size: 10px;
          }
          td {
            font-size: 10px;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-row {
            background-color: #e3f2fd;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 9px;
            color: #666;
          }
    `;

    const bodyHtml = `
      <div class="print-page">
        <div class="header">
          <h1>RÉCAPITULATIF HEURES SUPPLÉMENTAIRES</h1>
          <div>${establishmentInfo?.name || 'Établissement scolaire'}</div>
          <div>Période: ${months[filters.month]} ${filters.year}</div>
        </div>

        <div class="stats">
          <div class="stat-item">
            <div class="stat-label">Nombre d'enregistrements</div>
            <div class="stat-value">${stats.count}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Total Heures</div>
            <div class="stat-value">${stats.totalHeures}h</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Total Heures Majorées</div>
            <div class="stat-value" style="color: #4caf50;">${stats.totalMajorees}h</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Personnel</th>
              <th>Horaires</th>
              <th class="text-right">Heures</th>
              <th>Type</th>
              <th class="text-right">Majoration</th>
              <th class="text-right">Heures Majorées</th>
              <th>Motif</th>
            </tr>
          </thead>
          <tbody>
            ${selected.map(ot => `
              <tr>
                <td>${formatDate(ot.date)}</td>
                <td>${ot.nomComplet}</td>
                <td class="text-center">${ot.heureDebut && ot.heureFin ? `${ot.heureDebut} - ${ot.heureFin}` : '-'}</td>
                <td class="text-right"><strong>${ot.nombreHeures}h</strong></td>
                <td>${getTypeLabel(ot.type)}</td>
                <td class="text-right">${ot.tauxMajoration}%</td>
                <td class="text-right"><strong>${(ot.nombreHeures * (1 + ot.tauxMajoration / 100)).toFixed(2)}h</strong></td>
                <td>${ot.motif}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="3" class="text-right">TOTAL</td>
              <td class="text-right"><strong>${stats.totalHeures}h</strong></td>
              <td colspan="2"></td>
              <td class="text-right"><strong style="color: #4caf50;">${stats.totalMajorees}h</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p>Service: ${userData.service || 'N/A'} - Chef de Service: ${userData.firstName} ${userData.lastName}</p>
        </div>
      </div>`;

    openPrintWindow(
      'Récapitulatif Heures Supplémentaires',
      bodyHtml,
      extraCss,
      'height=900,width=1200',
      { singlePage: false, pageMargin: '10mm', orientation: 'landscape' }
    );
  };

  const handlePrint = (overtime) => {
    
    const formatDate = (date) => {
      if (!date) return 'N/A';
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    const extraCss = `
          .print-page {
            font-size: 12px;
            line-height: 1.4;
            color: #333;
          }
          .header {
            text-align: center; 
            margin-bottom: 15px; 
            padding: 8px;
            border-bottom: 2px solid #333;
          }
          .school-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .school-info {
            font-size: 11px;
            margin-bottom: 2px;
          }
          .section { 
            margin-bottom: 12px; 
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 3px;
          }
          .section h2 {
            font-size: 14px;
            margin: 0 0 8px 0;
            color: #2c3e50;
          }
          .info-line { 
            margin: 6px 0;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 1px dotted #ccc;
            padding-bottom: 3px;
            min-height: 18px;
          }
          .label {
            font-weight: bold;
            width: 40%;
            font-size: 11px;
          }
          .value {
            width: 60%;
            font-size: 11px;
            text-align: right;
          }
          .signatures {
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
            page-break-inside: avoid;
          }
          .signature-box {
            width: 45%;
            text-align: center;
          }
          .signature-line {
            margin-top: 50px;
            border-top: 1px solid #000;
            padding-top: 3px;
            font-weight: bold;
            font-size: 10px;
          }
          .date-line {
            margin-top: 10px;
            font-size: 9px;
          }
          .motif-section {
            background-color: #f8f9fa;
            padding: 8px;
            border-left: 3px solid #007bff;
            margin: 8px 0;
          }
          .motif-section h3 {
            margin-bottom: 4px;
            font-size: 13px;
          }
          .motif-section p {
            margin: 0;
            font-size: 11px;
            line-height: 1.3;
          }
          .highlight {
            background-color: #fff3cd;
            padding: 8px;
            border-left: 3px solid #ffc107;
            margin: 8px 0;
            font-weight: bold;
          }
          @media print {
            .section {
              break-inside: avoid;
            }
            .signatures {
              break-inside: avoid;
            }
          }
    `;

    const bodyHtml = `
      <div class="print-page">
        <div class="header">
          <div class="school-name">${establishmentInfo?.name || 'Établissement scolaire'}</div>
          ${establishmentInfo?.academie ? `<div class="school-info">Académie: ${establishmentInfo.academie}</div>` : ''}
          ${establishmentInfo?.anneeScolaire ? `<div class="school-info">Année scolaire: ${establishmentInfo.anneeScolaire}</div>` : ''}
          <h1>FICHE HEURES SUPPLÉMENTAIRES</h1>
        </div>
        
        <div class="section">
          <h2>Informations du Personnel</h2>
          <div class="info-line">
            <span class="label">Nom complet:</span>
            <span class="value">${overtime.nomComplet}</span>
          </div>
          <div class="info-line">
            <span class="label">Service:</span>
            <span class="value">${overtime.service || 'Non spécifié'}</span>
          </div>
        </div>

        <div class="section">
          <h2>Détails des Heures Supplémentaires</h2>
          <div class="info-line">
            <span class="label">Date:</span>
            <span class="value">${formatDate(overtime.date)}</span>
          </div>
          ${overtime.heureDebut && overtime.heureFin ? `
          <div class="info-line">
            <span class="label">Horaires:</span>
            <span class="value">${overtime.heureDebut} - ${overtime.heureFin}</span>
          </div>
          ` : ''}
          <div class="info-line">
            <span class="label">Nombre d'heures:</span>
            <span class="value">${overtime.nombreHeures} heure${overtime.nombreHeures > 1 ? 's' : ''}</span>
          </div>
          <div class="info-line">
            <span class="label">Type:</span>
            <span class="value">${getTypeLabel(overtime.type)}</span>
          </div>
          <div class="info-line">
            <span class="label">Taux de majoration:</span>
            <span class="value">${overtime.tauxMajoration}%</span>
          </div>
        </div>

        <div class="highlight">
          <div style="text-align: center;">
            Heures majorées: ${(overtime.nombreHeures * (1 + overtime.tauxMajoration / 100)).toFixed(2)} heures
          </div>
        </div>

        ${overtime.motif ? `
        <div class="motif-section">
          <h3>Motif</h3>
          <p>${overtime.motif}</p>
        </div>
        ` : ''}

        <div class="signatures">
          <div class="signature-box">
            <div>Date et signature de l'agent</div>
            <div class="signature-line">${overtime.nomComplet}</div>
            <div class="date-line">Date: _______________</div>
          </div>
          <div class="signature-box">
            <div>Date et signature du chef de service</div>
            <div class="signature-line">${userData.firstName || ''} ${userData.lastName || ''}</div>
            <div class="date-line">Date: _______________</div>
          </div>
        </div>

        <div style="margin-top: 20px; text-align: center; font-size: 10px; color: #666;">
          Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
        </div>
      </div>`;

    openPrintWindow(
      `Fiche Heures Supplémentaires - ${overtime.nomComplet}`,
      bodyHtml,
      extraCss,
      'height=900,width=1000',
      { singlePage: false, pageMargin: '10mm 8mm' }
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      normal: 'Heures normales',
      nuit: 'Heures de nuit',
      dimanche: 'Dimanche',
      ferie: 'Jour férié'
    };
    return types[type] || type;
  };

  const getTauxLabel = (taux) => {
    return `${taux}%`;
  };

  const calculateStats = () => {
    const totalHeures = filteredOvertimes.reduce((sum, ot) => sum + (ot.nombreHeures || 0), 0);
    const totalMajorees = filteredOvertimes.reduce((sum, ot) => 
      sum + (ot.nombreHeures * (1 + ot.tauxMajoration / 100)), 0
    );
    const nombrePersonnel = new Set(filteredOvertimes.map(ot => ot.personnelId)).size;

    return {
      totalHeures: totalHeures.toFixed(2),
      totalMajorees: totalMajorees.toFixed(2),
      nombrePersonnel,
      nombreEnregistrements: filteredOvertimes.length
    };
  };

  const stats = calculateStats();

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/chef-service/dashboard')}
            sx={{ mb: 1 }}
          >
            Retour au tableau de bord
          </Button>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" />
            Gestion des Heures Supplémentaires
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Suivi et gestion des heures supplémentaires du personnel
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter des heures
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Heures
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalHeures}h
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Heures Majorées
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalMajorees}h
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="info" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Personnel Concerné
                  </Typography>
                  <Typography variant="h5">
                    {stats.nombrePersonnel}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Enregistrements
                  </Typography>
                  <Typography variant="h5">
                    {stats.nombreEnregistrements}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mois</InputLabel>
              <Select
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                label="Mois"
              >
                {months.map((month, index) => (
                  <MenuItem key={index} value={index}>{month}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Année</InputLabel>
              <Select
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                label="Année"
              >
                {[2024, 2025, 2026].map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Personnel</InputLabel>
              <Select
                value={filters.personnelId}
                onChange={(e) => setFilters({ ...filters, personnelId: e.target.value })}
                label="Personnel"
              >
                <MenuItem value="all">Tous</MenuItem>
                {personnel.map(person => (
                  <MenuItem key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Barre d'actions pour la sélection */}
      {selectedOvertimes.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" component="span">
                {selectedOvertimes.length} élément{selectedOvertimes.length > 1 ? 's' : ''} sélectionné{selectedOvertimes.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: 'inline' }}>
                Total: {calculateSelectedStats().totalHeures}h | Majorées: {calculateSelectedStats().totalMajorees}h
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PrintOutlinedIcon />}
                onClick={handlePrintSelected}
              >
                Imprimer la sélection
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedOvertimes([])}
              >
                Désélectionner
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedOvertimes.length > 0 && selectedOvertimes.length < filteredOvertimes.length}
                  checked={filteredOvertimes.length > 0 && selectedOvertimes.length === filteredOvertimes.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Personnel</TableCell>
              <TableCell>Horaires</TableCell>
              <TableCell align="right">Heures</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Majoration</TableCell>
              <TableCell align="right">Heures Majorées</TableCell>
              <TableCell>Motif</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOvertimes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucune heure supplémentaire enregistrée pour cette période
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOvertimes.map((overtime) => (
                <TableRow 
                  key={overtime.id} 
                  hover
                  selected={selectedOvertimes.includes(overtime.id)}
                  onClick={() => handleSelectOne(overtime.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedOvertimes.includes(overtime.id)}
                      onChange={() => handleSelectOne(overtime.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {overtime.date?.toDate ? 
                      overtime.date.toDate().toLocaleDateString('fr-FR') : 
                      new Date(overtime.date).toLocaleDateString('fr-FR')
                    }
                  </TableCell>
                  <TableCell>{overtime.nomComplet}</TableCell>
                  <TableCell>
                    {overtime.heureDebut && overtime.heureFin ? 
                      `${overtime.heureDebut} - ${overtime.heureFin}` : 
                      '-'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <strong>{overtime.nombreHeures}h</strong>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getTypeLabel(overtime.type)} 
                      size="small"
                      color={overtime.type === 'normal' ? 'default' : 'primary'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={getTauxLabel(overtime.tauxMajoration)} 
                      size="small"
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <strong style={{ color: '#4caf50' }}>
                      {(overtime.nombreHeures * (1 + overtime.tauxMajoration / 100)).toFixed(2)}h
                    </strong>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={overtime.motif || ''}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {overtime.motif}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Imprimer">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePrint(overtime)}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Enregistrer des heures supplémentaires
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Personnel</InputLabel>
                  <Select
                    name="personnelId"
                    value={formData.personnelId}
                    onChange={handleFormChange}
                    label="Personnel"
                  >
                    {personnel.map(person => (
                      <MenuItem key={person.id} value={person.id}>
                        {person.firstName} {person.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    label="Type"
                  >
                    <MenuItem value="normal">Heures normales</MenuItem>
                    <MenuItem value="nuit">Heures de nuit</MenuItem>
                    <MenuItem value="dimanche">Dimanche</MenuItem>
                    <MenuItem value="ferie">Jour férié</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Heure début"
                  type="time"
                  name="heureDebut"
                  value={formData.heureDebut}
                  onChange={handleFormChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Heure fin"
                  type="time"
                  name="heureFin"
                  value={formData.heureFin}
                  onChange={handleFormChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Nombre d'heures"
                  type="number"
                  name="nombreHeures"
                  value={formData.nombreHeures}
                  onChange={handleFormChange}
                  inputProps={{
                    min: 0,
                    step: 0.25
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">h</InputAdornment>,
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Taux de majoration</InputLabel>
                  <Select
                    name="tauxMajoration"
                    value={formData.tauxMajoration}
                    onChange={handleFormChange}
                    label="Taux de majoration"
                  >
                    <MenuItem value="0">0% (Heures normales sans majoration)</MenuItem>
                    <MenuItem value="25">25% (Heures normales)</MenuItem>
                    <MenuItem value="50">50% (Nuit/Dimanche)</MenuItem>
                    <MenuItem value="100">100% (Jours fériés)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motif"
                  name="motif"
                  value={formData.motif}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  required
                  placeholder="Ex: Remplacement, événement spécial, urgence..."
                />
              </Grid>

              {formData.nombreHeures && formData.tauxMajoration && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    Heures majorées: <strong>
                      {(parseFloat(formData.nombreHeures) * (1 + parseInt(formData.tauxMajoration) / 100)).toFixed(2)} heures
                    </strong>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog}>
              Annuler
            </Button>
            <Button type="submit" variant="contained">
              Enregistrer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Container>
  );
};

export default OvertimeManagement;
